import React, { useState, useMemo } from 'react';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { getProvidersByType, getProviderById } from '@/providers/database';
import { ProviderGrid } from './ProviderGrid';
import { ProviderAuthForm } from './ProviderAuthForm';
import { databasesApi } from '../services/api';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
/**
 * Multi-step wizard for creating databases (Drawer version)
 *
 * @example
 * <CreateDatabaseModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={(database) => handleSuccess(database)}
 * />
 */
export interface CreateDatabaseModalProps {
  /** Drawer open state */
  isOpen: boolean;
  /** Callback when drawer should close */
  onClose: () => void;
  /** Callback when database is created successfully */
  onSuccess?: (database: any) => void;
}

type WizardStep = 'basic-info' | 'integration' | 'authentication';
type DatabaseTypeValue = 'properties' | 'vehicles' | 'products' | 'services' | 'documents';

interface DatabaseFormData {
  name: string;
  type: DatabaseTypeValue | null;
  description?: string;
  selectedProviderId?: string | null;
  credentials?: Record<string, any>;
}

const databaseTypes = [
  {
    type: 'properties' as const,
    label: 'Imóveis',
    description: 'Gestão completa de propriedades com múltiplas oportunidades de negócio',
    icon: 'home',
    color: '#3b82f6', // blue
  },
  {
    type: 'vehicles' as const,
    label: 'Veículos',
    description: 'Catálogo de veículos com informações detalhadas',
    icon: 'truck',
    color: '#8b5cf6', // purple
  },
  {
    type: 'products' as const,
    label: 'Produtos',
    description: 'Gestão de produtos com controle de estoque',
    icon: 'package',
    color: '#10b981', // green
  },
  {
    type: 'services' as const,
    label: 'Serviços',
    description: 'Catálogo de serviços oferecidos',
    icon: 'tool',
    color: '#f59e0b', // amber
  },
  {
    type: 'documents' as const,
    label: 'Documentos',
    description: 'Base de conhecimento e artigos',
    icon: 'file-text',
    color: '#ef4444', // red
  },
];

const stepNumbers: Record<WizardStep, number> = {
  'basic-info': 1,
  'integration': 2,
  'authentication': 3,
};

const stepTitles: Record<WizardStep, string> = {
  'basic-info': 'Informações Básicas',
  'integration': 'Integração',
  'authentication': 'Autenticação',
};

export const CreateDatabaseModal: React.FC<CreateDatabaseModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic-info');
  const [formData, setFormData] = useState<DatabaseFormData>({
    name: '',
    type: null,
    description: '',
    selectedProviderId: null,
    credentials: {},
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Buscar providers disponíveis para o tipo selecionado
  const availableProviders = useMemo(() => {
    if (!formData.type) return [];
    return getProvidersByType(formData.type);
  }, [formData.type]);

  // Provider selecionado
  const selectedProvider = useMemo(() => {
    if (!formData.selectedProviderId || formData.selectedProviderId === 'none') return null;
    return getProviderById(formData.selectedProviderId);
  }, [formData.selectedProviderId]);

  const handleNext = () => {
    if (currentStep === 'basic-info') {
      // Validação step 1
      if (!formData.name || formData.name.trim().length < 2) {
        setError('Nome da base deve ter pelo menos 2 caracteres');
        return;
      }
      if (!formData.type) {
        setError('Selecione o tipo da base de dados');
        return;
      }
      setError(null);
      setCurrentStep('integration');
    } else if (currentStep === 'integration') {
      // Se selecionou provider, vai para authentication
      // Se selecionou "Nenhum", finaliza
      if (formData.selectedProviderId && formData.selectedProviderId !== 'none') {
        setCurrentStep('authentication');
      } else {
        handleCreateDatabase();
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 'authentication') {
      setCurrentStep('integration');
    } else if (currentStep === 'integration') {
      setCurrentStep('basic-info');
    }
  };

  const handleCreateDatabase = async (authData?: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type as any,
        description: formData.description?.trim() || undefined,
        providerId: formData.selectedProviderId !== 'none' ? (formData.selectedProviderId || undefined) : undefined,
        integrationCredentials: authData || formData.credentials,
      };

      console.log('Creating database:', payload);

      // Chamada real à API
      const database = await databasesApi.create(payload);

      console.log('Database created successfully:', database);

      if (onSuccess) {
        onSuccess(database);
      }

      handleClose();
    } catch (err: any) {
      console.error('Error creating database:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao criar base de dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep('basic-info');
    setFormData({
      name: '',
      type: null,
      description: '',
      selectedProviderId: null,
      credentials: {},
    });
    setError(null);
    onClose();
  };

  const isStep1Valid = formData.name.trim().length >= 2 && formData.type !== null;

  // Compute dynamic title and subtitle for the Drawer header
  const stepTitle = stepTitles[currentStep];
  const stepSubtitle = `Passo ${stepNumbers[currentStep]} de 3`;

  // Footer content
  const footerContent = (
    <div className="flex items-center justify-between w-full">
      <div>
        {currentStep !== 'basic-info' && (
          <Button variant="secondary" onClick={handleBack} disabled={isLoading}>
            Voltar
          </Button>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancelar
        </Button>
        {currentStep === 'basic-info' && (
          <Button variant="primary" onClick={handleNext} disabled={!isStep1Valid}>
            Próximo
          </Button>
        )}
        {currentStep === 'integration' && (
          <Button
            variant="primary"
            onClick={handleNext}
            disabled={!formData.selectedProviderId}
          >
            {formData.selectedProviderId === 'none' ? 'Criar Base de Dados' : 'Próximo'}
          </Button>
        )}
        {currentStep === 'authentication' && (
          <Button
            variant="primary"
            onClick={() => handleCreateDatabase(formData.credentials)}
            disabled={isLoading || (selectedProvider?.authFields.filter(f => f.required).some(f => !formData.credentials?.[f.name]) ?? true)}
          >
            {isLoading ? 'Criando...' : 'Criar Base de Dados'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      icon={<Icon name="database" className="h-5 w-5" />}
      title={stepTitle}
      subtitle={stepSubtitle}
      footer={footerContent}
      disabled={isLoading}
    >
      <div className="px-6 py-5">
      {/* Barra de progresso do wizard (mockup `.wz-steps`/`.wz-dot` — 3 barras 4px que preenchem por passo) */}
      <div className="flex items-center gap-1.5 mb-5">
        {[1, 2, 3].map((n) => (
          <span
            key={n}
            className="h-1 flex-1 rounded-full transition-colors"
            style={{ backgroundColor: n <= stepNumbers[currentStep] ? 'var(--theme-primary)' : 'var(--theme-card-border)' }}
          />
        ))}
      </div>
      {error && (
        <div
          className="mb-4 p-3 rounded-lg border"
          style={{
            backgroundColor: '#fef2f2',
            borderColor: '#fecaca',
            color: '#b91c1c',
          }}
        >
          {error}
        </div>
      )}

      {/* Step 1: Basic Info */}
      {currentStep === 'basic-info' && (
        <div className="space-y-6">
          {/* Name */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Nome da Base de Dados *
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ex: Catálogo de Produtos"
            />
          </div>

          {/* Description */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: '#1e293b' }}
            >
              Descrição (opcional)
            </label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrição da base de dados..."
              rows={3}
              resize="none"
            />
          </div>

          {/* Type Selection */}
          <div>
            <label
              className="block text-sm font-medium mb-3"
              style={{ color: '#1e293b' }}
            >
              Tipo da Base *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {databaseTypes.map((dbType) => {
                const isSelected = formData.type === dbType.type;

                return (
                  <button
                    key={dbType.type}
                    onClick={() => setFormData({ ...formData, type: dbType.type })}
                    className="p-4 rounded-lg border-2 text-left transition-all"
                    style={{
                      borderColor: isSelected ? dbType.color : '#e5e7eb',
                      backgroundColor: isSelected ? `${dbType.color}08` : '#ffffff',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="p-2 rounded-lg flex-shrink-0"
                        style={{
                          backgroundColor: `${dbType.color}20`,
                          color: dbType.color,
                        }}
                      >
                        <Icon name={dbType.icon as IconName} className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-sm font-semibold"
                          style={{ color: '#1e293b' }}
                        >
                          {dbType.label}
                        </h3>
                        <p
                          className="text-xs mt-1"
                          style={{ color: '#64748b' }}
                        >
                          {dbType.description}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Integration */}
      {currentStep === 'integration' && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: '#64748b' }}>
            Conecte esta base a uma integração externa para sincronização automática de dados ou continue sem integração.
          </p>

          <ProviderGrid
            providers={availableProviders}
            selectedProviderId={formData.selectedProviderId ?? null}
            onSelectProvider={(providerId) =>
              setFormData({ ...formData, selectedProviderId: providerId })
            }
            showNoneOption={true}
          />
        </div>
      )}

      {/* Step 3: Authentication */}
      {currentStep === 'authentication' && selectedProvider && (
        <div className="space-y-4">
          <ProviderAuthForm
            provider={selectedProvider}
            credentials={formData.credentials || {}}
            onCredentialsChange={(credentials) =>
              setFormData({ ...formData, credentials })
            }
          />
        </div>
      )}
      </div>
    </Drawer>
  );
};

CreateDatabaseModal.displayName = 'CreateDatabaseModal';
